import axios, { AxiosInstance } from 'axios';
import i18next from 'i18next';
import { toast } from 'react-toastify';
import { GetAxiosInstanceProps } from '~/common/react/utils/networkUtil';
import { AppError } from '~/common/typescript/models/AppError';
import { AppSuccess } from '~/common/typescript/models/AppSuccess';
import { API_URL_ADMIN } from '~/config/config';

/**
 * base axios instance for connect to backend api
 *
 * @param noAuth : no need bearer authorization -> for some apis: login, register, and other public apis
 * @returns: AxiosInstance
 *
 * @usage getAxiosInstance().post(url, {...data}).then( (data) => {console.log(data)} )
 * @usage const data = await getAxiosInstance().get()
 */
const getAxiosInstanceImpl = (props?: GetAxiosInstanceProps): AxiosInstance => {
  let headers = {};
  {
    const token = localStorage.jwt;
    if (token && !props?.noAuth) {
      headers = {
        Authorization: `Bearer ${token}`,
      };
    }
  }
  const baseURL =
    (typeof window !== 'undefined' && localStorage.getItem('API_URL_ADMIN')) ||
    API_URL_ADMIN;
  const instance = axios.create({
    baseURL: baseURL,
    // timeout: 1000,
    headers: headers,
  });

  // Request Interceptor
  instance.interceptors.request.use(
    (config) => {
      return config;
    },
    (error) => {
      // Handle request errors
      return Promise.reject(error);
    },
  );

  // Response Interceptor
  instance.interceptors.response.use(
    (response) => {
      handleGlobalSuccessEvent(response);
      return response;
    },
    (error) => {
      if (!props?.hideSystemError && !(error.config as any)?.hideGlobalError) {
        handleGlobalErrorEvent(error);
      }
      return Promise.reject(error);
    },
  );
  return instance;
};

const handleGlobalSuccessEvent = (response: any) => {
  if (!response.data.i18n) {
    // it is an AppSuccess
    return;
  }
  const appSuccess: AppSuccess<any> = response.data;
  if (appSuccess.hidden) {
    return;
  }

  if (appSuccess.i18n) {
    let currentLanguage = i18next.language;
    let message = appSuccess.i18n.vi;
    {
      if (currentLanguage === 'en') {
        appSuccess.i18n.en;
      }
    }
    toast.success(message, {
      position: 'top-right',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: 'colored',
    });
  }
};

const handleGlobalErrorEvent = (error: any) => {
  if (error.response) {
    let currentLanguage = i18next.language;

    if (!error.response.data.error) {
      // it is not our AppError
      return;
    }
    const appError: AppError = error.response.data;
    if (appError.error.hidden) {
      return;
    }
    let message = appError.error.i18n ? appError.error.i18n.vi : appError.error.message;
    {
      if (currentLanguage === 'en') {
        appError.error.i18n?.en;
      }
    }

    toast.error(message, {
      position: 'top-right',
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: 'colored',
    });

    if (error.response.status === 401) {
      localStorage.removeItem('jwt');
      // window.location.reload();
    }
  } else {
    const msg = error?.message || '';
    if (
      msg === 'Network Error' ||
      error?.code === 'ERR_NETWORK' ||
      msg.toLowerCase() === 'network error'
    ) {
      return;
    }
    if (msg) {
      toast(msg);
    }
  }
};

export { getAxiosInstanceImpl };
